import { useTranslation } from "react-i18next";
import Card from "../../../components/Card/Card";
import CardHeader from "../../../components/Card/CardHeader";
import CardBody from "../../../components/Card/CardBody";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    IconButton,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Portal,
    Select,
    Stack,
    Text,
    Textarea,
    Tooltip,
    useColorModeValue,
    useDisclosure
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { processesService, indeferPattern, diligenciaPattern, deferPattern, JUDGE_OPTIONS, JUDGMENT_OPTIONS, PROCESS_STATUS } from "../../../services/processesService";
import ProcessDetail from "./ProcessDetail";
import { useNavigate, useParams } from "react-router-dom";
import { getSneDate } from "../../../utils/traffic-violation-utils";
import { checkErrors, hasError } from "../../../utils/forms-utils";
import { ROUTES } from "../../../constants/routes-path";
import { getKind, getLimitDate } from "../../../utils/process-utils";
import ProcessDecisionInference from "./ProcessDecisionInference";
import { AiOutlineRobot } from "react-icons/ai";
import { keyframes } from "@emotion/react";
import { isValidFactsAndFundaments } from "../../../utils/process-utils";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { FaQuestionCircle } from "react-icons/fa";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import { ENABLE_DECISION_INFERENCE } from "constants/config";

const ProcessJudgementPanel = ({ showActions = true }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const [processData, setProcessData] = useState([]);
    const [judgmentOptions, setJudgmentOptions] = useState([PROCESS_STATUS.ACCEPTED, PROCESS_STATUS.REJECTED]);
    const textColor = useColorModeValue("gray.700", "white");
    const [inputError, setInputError] = useState({});
    const [formData, setFormData] = useState(undefined);
    const [currentStatus, setCurrentStatus] = useState(null);
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [loadingSuggestion, setLoadingSuggestion] = useState(false);
    const showToast = useCustomToast();
    const dialogRef = useRef(undefined);
    const [onConfirmDecision, setOnConfirmDecision] = useState(() => () => { });
    const [usedInference, setUsedInference] = useState(false);
    const [hasInference, setHasInference] = useState(false);

    const rotateBg = keyframes`
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
    `;

    const ids = {
        fatos: "fatos",
        fundamentos: "fundamentos",
        decisao: "decisao",
        decisaoRelator: "decisao_relator",
        decisaoFinal: "decisao_final"
    };

    const initJudgementFormData = (initialData) => {
        const formDataToReturn = {
            [ids.fatos]: {
                value: initialData?.fatos ||
                    t("process.judgment.default-facts", {
                        numeroAit: initialData?.infracao?.numero_ait || "",
                        amparoLegal: initialData?.infracao?.amparo_legal || ""
                    }),
                validations: [{ required: true }],
            },
            [ids.fundamentos]: {
                value: initialData?.fundamentos || "",
                validations: [{ required: true }],
            },
            [ids.decisao]: {
                value: initialData?.decisao || "",
                validations: [{ required: true }],
            }
        };

        if (initialData?.process_judgement_needed) {
            formDataToReturn[ids.decisaoRelator] = {
                value: initialData?.decisao_relator || "",
                validations: [{ required: true }],
            }
        } else {
            formDataToReturn[ids.decisaoFinal] = {
                value: initialData?.decisao_final || "",
                validations: [{ required: true }],
            }
        }

        return formDataToReturn;
    };

    const isFormInvalid =
        formData && inputError ? hasError({ formData, inputError }) : true;

    const toJudgementDTO = (statusSet) => {
        const dto = {
            fatos: formData[ids.fatos].value.trim(),
            fundamentos: formData[ids.fundamentos].value.trim(),
            decisao: formData[ids.decisao].value.trim(),
            status: statusSet ?? currentStatus,
            used_inference: usedInference,
            has_inference: hasInference,
        };
        if (processData?.process_judgement_needed) {
            dto.decisao_relator = formData[ids.decisaoRelator].value;
        } else {
            dto.decisao_final = formData[ids.decisaoFinal].value;
        }
        return dto;
    };

    useEffect(() => {
        if (id) {
            fetchProcess();
        }
    }, [id]);

    const fetchProcess = async () => {
        try {
            const process = await processesService.find(id);
            setProcessData(process);
            if (process?.processes_diligence_status && !judgmentOptions.includes(PROCESS_STATUS.UNDER_DILIGENCE)) {
                setJudgmentOptions((prev) => [...prev, PROCESS_STATUS.UNDER_DILIGENCE]);
            }
            setFormData(initJudgementFormData(process));
        } catch (error) {
            showToast({ title: t("process.messages.errors.find"), status: "error" });
            redirectToList();
        }
    }

    const isEditable = () => processData?.process_responsible_needed && processData?.process_judgement_needed;

    const redirectToList = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.processes);
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;

        const newFormData = {
            ...formData,
            [id]: { ...formData[id], value: value },
        };

        if (id === ids.decisao && formData?.[ids.decisaoRelator]) {
            const suggested = mapDecisionTextToRelatorOption(value);
            newFormData[ids.decisaoRelator] = {
                ...formData[ids.decisaoRelator],
                value: suggested,
            };
        }

        setFormData(newFormData);

        let formValidationErrors = checkErrors({ formData: newFormData, inputError: inputError, name: id })

        setInputError(formValidationErrors);
    };

    const handleReject = () => {
        const decisionText = formData?.[ids.decisao]?.value?.trim();
        if (!decisionText) {
            showToast({ title: t("process.messages.errors.missing-decision"), status: "error" });
            return;
        }
        setCurrentStatus(JUDGMENT_OPTIONS.INDEFERIDA);
        handleSave(JUDGMENT_OPTIONS.INDEFERIDA);
    };

    const handleAccept = () => {
        setCurrentStatus(JUDGMENT_OPTIONS.DEFERIDA);
        handleSave(JUDGMENT_OPTIONS.DEFERIDA);
    };

    const canShowSuggestion = () => {
        const fatos = formData?.fatos?.value ?? "";
        const fundamentos = formData?.fundamentos?.value ?? "";

        return isValidFactsAndFundaments(fatos) && isValidFactsAndFundaments(fundamentos);
    };

    const handleUseSuggestion = (suggestionText) => {
        const decisao = mapDecisionTextToRelatorOption(suggestionText);
        setFormData((prev) => ({
            ...prev,
            [ids.decisao]: {
                ...prev[ids.decisao],
                value: suggestionText,
            },
            [ids.decisaoRelator]: {
                ...prev[ids.decisaoRelator],
                value: decisao,
            },
        }));

        setInputError((prev) => ({
            ...prev,
            [ids.decisao]: undefined,
            [ids.decisaoRelator]: undefined,
        }));
        setHasInference(true);
        setUsedInference(!!decisao);
    };

    const handleSuggestionRequested = (success) => {
        setHasInference(success);
        setUsedInference(false);
    }

    const mapDecisionTextToRelatorOption = (text) => {
        if (!text) return "";

        const judgement = text.toLowerCase();

        if (indeferPattern.test(judgement) && judgmentOptions.includes(JUDGMENT_OPTIONS.INDEFERIDA)) {
            return JUDGMENT_OPTIONS.INDEFERIDA;
        }

        if (diligenciaPattern.test(judgement) && judgmentOptions.includes(JUDGMENT_OPTIONS.DILIGENCIA)) {
            return JUDGMENT_OPTIONS.DILIGENCIA;
        }

        if (deferPattern.test(judgement) && judgmentOptions.includes(JUDGMENT_OPTIONS.DEFERIDA)) {
            return JUDGMENT_OPTIONS.DEFERIDA;
        }

        return "";
    };

    const suggestedRelatorDecision = mapDecisionTextToRelatorOption(formData?.[ids.decisao]?.value || "");

    const confirmDecision = () => {
        if (!formData) return false;

        const decisionValue = formData[ids.decisao]?.value || "";
        const decisionReporterValue = formData[ids.decisaoRelator]?.value || "";

        const mappedDecision = mapDecisionTextToRelatorOption(decisionValue);

        const areDifferent = mappedDecision.trim().toLowerCase() !== decisionReporterValue.trim().toLowerCase();

        return mappedDecision !== "" && decisionReporterValue !== "" && areDifferent;
    };

    const handleJudge = async (statusSet) => {
        try {
            await processesService.judge(id, toJudgementDTO(statusSet));
            showToast({
                title: t("process.messages.success.judgement"),
                status: "success",
            });
            redirectToList();
        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
    };

    const handleSave = async (statusSet) => {
        if (confirmDecision()) {
            setOnConfirmDecision(() => () => handleJudge(statusSet));
            dialogRef.current?.displayDialog({
                title: t("process.judgment.decision-confirm-title"),
                message: t("process.judgment.decision-confirm-message"),
            });
        } else {
            await handleJudge(statusSet);
        }
    };


    return (
        <>
            <Stack w="100%" direction={{ sm: "column", md: "column", lg: "row" }}>
                <ProcessDetail flex={{ md: "none", lg: "60%" }} initialData={processData}></ProcessDetail>
                <Flex flex={{ md: "none", lg: "40%" }} direction="column" pt={{ sm: "0px", lg: "75px" }}>
                    <Card>
                        <CardHeader>
                            <Flex align="center" justify="space-between">
                                <Text color={textColor} fontSize="lg" fontWeight="bold">
                                    {t("process.judgment.title")}
                                </Text>
                                {ENABLE_DECISION_INFERENCE && processData?.process_judgement_needed && (
                                    <Popover
                                        isOpen={isOpen}
                                        onOpen={onOpen}
                                        onClose={onClose}
                                        placement="left-start"
                                    >
                                        <Tooltip
                                            placement="top"
                                            label={
                                                canShowSuggestion()
                                                    ? t("process.judgment.get-suggestion-tooltip-available")
                                                    : t("process.judgment.get-suggestion-tooltip-unavailable")
                                            }
                                            shouldWrapChildren
                                            hasArrow
                                        >
                                            <PopoverTrigger>
                                                <IconButton
                                                    icon={<AiOutlineRobot color="white" size="20px" />}
                                                    aria-label={t("process.judgment.get-suggestion")}
                                                    variant="solid"
                                                    borderRadius="full"
                                                    w="36px" h="36px"
                                                    isDisabled={!canShowSuggestion()}
                                                    bgGradient="linear(to-r, #ff4800, #da54d8, #0092f4)"
                                                    backgroundSize="300% 300%"
                                                    _hover={{
                                                        bgGradient: "linear(to-r, #0092f4, #da54d8, #ff4800)",
                                                    }}
                                                    sx={{
                                                        backgroundImage: "linear(to-r, #ff4800, #da54d8, #0092f4)",
                                                        backgroundSize: "300% 300%",
                                                        animation: loadingSuggestion ? `${rotateBg} 2s linear infinite` : "none",
                                                        backgroundPosition: loadingSuggestion ? undefined : "center",
                                                    }}
                                                />
                                            </PopoverTrigger>
                                        </Tooltip>

                                        <Portal>
                                            <PopoverContent
                                                zIndex={20}
                                                boxShadow="lg"
                                                width={{ base: "90vw", md: "400px", lg: "350px", xl: "310px" }}
                                                maxW="100vw"
                                            >
                                                <ProcessDecisionInference
                                                    onClose={onClose}
                                                    processId={id}
                                                    onUseSuggestion={handleUseSuggestion}
                                                    canInfer={canShowSuggestion()}
                                                    loadingSuggestion={loadingSuggestion}
                                                    setLoadingSuggestion={setLoadingSuggestion}
                                                    data={formData}
                                                    onSuggestionRequested={handleSuggestionRequested}
                                                />
                                            </PopoverContent>
                                        </Portal>
                                    </Popover>
                                )}
                            </Flex>
                        </CardHeader>
                        <CardBody>
                            <Stack spacing={4}>
                                <Box>
                                    <Text color={textColor} fontSize="md">
                                        <Text as="span" fontWeight="bold">
                                            {t("process.judgment.sne-date")}:
                                        </Text>{" "}
                                        {getSneDate(processData?.infracao) || t("process.judgment.not-joined")}
                                    </Text>
                                </Box>

                                {processData?.observacoes && (
                                    <Box>
                                        <Text color={textColor} fontSize="md">
                                            <Text as="span" fontWeight="bold">
                                                {t("process.judgment.observations")}:
                                            </Text>{" "}
                                            {processData?.observacoes}
                                        </Text>
                                    </Box>
                                )}

                                {isEditable() ? (
                                    <>
                                        {getLimitDate(processData) && (
                                            <Box>
                                                <Text color={textColor} fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("process.judgment.situation")}:
                                                    </Text>{" "}
                                                    {getKind(processData)}
                                                </Text>
                                            </Box>
                                        )}

                                        {processData?.observacoes_revisao && (
                                            <Box>
                                                <Text color={textColor} fontSize="md">
                                                    <Text as="span" fontWeight="bold">
                                                        {t("process.judgment.review-observations")}:
                                                    </Text>{" "}
                                                    {processData?.observacoes_revisao}
                                                </Text>
                                            </Box>
                                        )}

                                        <FormControl isRequired isInvalid={inputError?.fatos}>
                                            <FormLabel>{t("process.details.tab-general.facts")}</FormLabel>
                                            <Textarea
                                                id={ids.fatos}
                                                value={formData?.fatos.value}
                                                onChange={handleInputChange}
                                            />
                                            <FormErrorMessage>{t(inputError?.fatos)}</FormErrorMessage>
                                        </FormControl>

                                        <FormControl isRequired isInvalid={inputError?.fundamentos}>
                                            <FormLabel>{t("process.details.tab-general.fundaments")}</FormLabel>
                                            <Textarea
                                                id={ids.fundamentos}
                                                value={formData?.fundamentos.value}
                                                onChange={handleInputChange}
                                            />
                                            <FormErrorMessage>{t(inputError?.fundamentos)}</FormErrorMessage>
                                        </FormControl>

                                        <FormControl isRequired isInvalid={inputError?.decisao}>
                                            <FormLabel>{t("process.details.tab-general.decision")}</FormLabel>
                                            <Textarea
                                                id={ids.decisao}
                                                value={formData?.decisao.value}
                                                onChange={handleInputChange}
                                            />
                                            <FormErrorMessage>{t(inputError?.decisao)}</FormErrorMessage>
                                        </FormControl>

                                        {processData?.process_judgement_needed ? (
                                            <FormControl isRequired isInvalid={inputError?.decisao_relator}>
                                                <FormLabel display="flex" alignItems="center">
                                                    {t("process.judgment.relator-decision")}
                                                    <Tooltip
                                                        label={
                                                            suggestedRelatorDecision
                                                                ? t(`process.judgment.relator-decision-suggestion.${JUDGE_OPTIONS[suggestedRelatorDecision]}`)
                                                                : t("process.judgment.relator-decision-suggestion.no-suggestion")
                                                        }
                                                        hasArrow
                                                        placement="top"
                                                    >
                                                        <Box as="span" ml="2" color="red.500" cursor="default">
                                                            <FaQuestionCircle />
                                                        </Box>
                                                    </Tooltip>
                                                </FormLabel>

                                                <Select
                                                    fontSize="sm"
                                                    id={ids.decisaoRelator}
                                                    name={ids.decisaoRelator}
                                                    value={formData[ids.decisaoRelator]?.value}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value=""></option>
                                                    {Object.entries(judgmentOptions).map(([key, label]) => (
                                                        <option key={key} value={label}>
                                                            {t(`process.judgment.process-types.${JUDGE_OPTIONS[label]}`)}
                                                        </option>
                                                    ))}
                                                </Select>
                                                <FormErrorMessage>{t(inputError?.decisao_relator)}</FormErrorMessage>
                                            </FormControl>
                                        ) : (
                                            <FormControl isRequired isInvalid={inputError?.decisao_final}>
                                                <FormLabel>{t("process.judgment.final-decision")}</FormLabel>
                                                <Select
                                                    fontSize="sm"
                                                    id={ids.decisaoFinal}
                                                    name={ids.decisaoFinal}
                                                    value={formData[ids.decisaoFinal]?.value}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value=""></option>
                                                    {Object.entries(judgmentOptions).map(([key, label]) => (
                                                        <option key={key} value={label}>
                                                            {t(`process.judgment.process-types.${JUDGE_OPTIONS[label]}`)}
                                                        </option>
                                                    ))}
                                                </Select>
                                                <FormErrorMessage>{t(inputError?.decisao_final)}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </>
                                ) : (
                                    <FormControl isInvalid={inputError?.decisao}>
                                        <FormLabel>{t("process.details.tab-general.decision")}</FormLabel>
                                        <Textarea
                                            id={ids.decisao}
                                            value={formData?.decisao.value}
                                            onChange={handleInputChange}
                                        />
                                        <FormErrorMessage>{t(inputError?.decisao)}</FormErrorMessage>
                                    </FormControl>
                                )}

                                {showActions && (
                                    isEditable() ? (
                                        <Stack w="100%" direction="row" justify="flex-end">
                                            <Button
                                                variant="dark"
                                                w="125px"
                                                fontSize="sm"
                                                _hover={isFormInvalid ? {} : undefined}
                                                onClick={() => handleSave()}
                                                isDisabled={isFormInvalid}
                                            >
                                                {t("system.actions.save")}
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Stack w="100%" direction="row" justify="space-between">
                                            <Button colorScheme="green" onClick={handleAccept}>
                                                {t("process.judgment.process-types.defer")}
                                            </Button>
                                            <Button colorScheme="red" onClick={handleReject}>
                                                {t("process.judgment.process-types.reject")}
                                            </Button>
                                        </Stack>
                                    )
                                )}
                            </Stack>
                        </CardBody>
                    </Card>
                    <ConfirmDialog ref={dialogRef} onConfirm={onConfirmDecision} />
                </Flex>
            </Stack>
        </>
    );
};

export default ProcessJudgementPanel;
